"use client"

import React, { useEffect, useRef, useState } from "react"
import { Pencil } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { cn } from "@/shared/lib/utils"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog"

type AppendOnlyNotesProps = {
  history?: string | null
  value: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  canEditHistory?: boolean
  onReplaceHistory?: (notes: string) => Promise<void>
}

export const AutoGrowNoteTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<typeof Textarea>
>(({ className, onChange, ...props }, forwardedRef) => {
  const localRef = useRef<HTMLTextAreaElement | null>(null)
  const assignRef = (element: HTMLTextAreaElement | null) => {
    ;(localRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element
    if (typeof forwardedRef === "function") forwardedRef(element)
    else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element
  }

  const resize = (element: HTMLTextAreaElement) => {
    element.style.height = "auto"
    element.style.height = `${Math.max(element.scrollHeight, 36)}px`
  }

  useEffect(() => {
    if (localRef.current) resize(localRef.current)
  }, [props.value])

  return (
    <Textarea
      {...props}
      ref={assignRef}
      rows={1}
      onChange={(event) => {
        resize(event.currentTarget)
        onChange?.(event)
      }}
      className={cn("min-h-0 resize-none overflow-hidden", className)}
    />
  )
})
AutoGrowNoteTextarea.displayName = "AutoGrowNoteTextarea"

/**
 * Shared append-only notes presentation used for read-only history plus a
 * single new entry. Timestamping belongs to the server so each entry receives
 * one authoritative author/time prefix, including multi-line notes.
 */
export function AppendOnlyNotes({
  history,
  value,
  onChange,
  label = "Work Order Notes",
  placeholder = "Add a new note...",
  disabled = false,
  canEditHistory = false,
  onReplaceHistory,
}: AppendOnlyNotesProps) {
  const [isEditingHistory, setIsEditingHistory] = useState(false)
  const [showEditWarning, setShowEditWarning] = useState(false)
  const [historyDraft, setHistoryDraft] = useState(history || "")
  const historyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const historyElement = historyRef.current
    if (historyElement) {
      historyElement.scrollTop = historyElement.scrollHeight
    }
  }, [history])

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label>{label}</Label>
          {canEditHistory && onReplaceHistory && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setShowEditWarning(true)}
              aria-label="Edit work order notes"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div
          ref={historyRef}
          className="max-h-[7rem] min-h-[60px] overflow-y-auto rounded-md border border-input bg-muted/50 p-3"
        >
          {history?.trim() ? (
            <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">{history}</pre>
          ) : (
            <p className="text-sm italic text-muted-foreground">No notes yet</p>
          )}
        </div>
      </div>
      <div>
        <Label htmlFor="newWorkOrderNote">Add New Note</Label>
        <AutoGrowNoteTextarea
          id="newWorkOrderNote"
          rows={1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="mt-1 min-h-0 resize-none overflow-hidden"
        />
      </div>
      <Dialog open={showEditWarning} onOpenChange={setShowEditWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Work Order Notes?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This changes only the Work Order Notes. It does not change Project General Notes; correct those separately if needed.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowEditWarning(false)}>Cancel</Button>
            <Button type="button" onClick={() => { setHistoryDraft(history || ""); setShowEditWarning(false); setIsEditingHistory(true) }}>
              I understand—edit notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {isEditingHistory && (
        <Dialog open onOpenChange={(open) => !open && setIsEditingHistory(false)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader><DialogTitle>Edit Work Order Notes</DialogTitle></DialogHeader>
            <Textarea value={historyDraft} onChange={(event) => setHistoryDraft(event.target.value)} rows={12} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditingHistory(false)}>Cancel</Button>
              <Button type="button" onClick={async () => { await onReplaceHistory?.(historyDraft); setIsEditingHistory(false) }}>
                Save work order notes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
